/**
 * SEOHead — Componente de SEO multilíngue (hreflang + Open Graph)
 * 
 * Injeta tags hreflang, og:locale e meta description em todas as páginas.
 * Usa useEffect pra manipular document.head diretamente (Preact não tem <Head>).
 */
import { useEffect } from 'preact/hooks';
import { useTranslation } from 'react-i18next';

/** Idiomas ativos no momento (expandir conforme ativar mais) */
const ACTIVE_LANGUAGES = [
  { code: 'pt-BR', ogLocale: 'pt_BR' },
  { code: 'en-US', ogLocale: 'en_US' },
];

/** Idioma padrão pra x-default */
const DEFAULT_LANG = 'pt-BR';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalPath?: string; // ex: /dashboard (sem query params)
}

/**
 * Gera URL com query param de idioma
 */
function buildLangUrl(path: string, langCode: string): string {
  const base = `${window.location.origin}${path}`;
  return `${base}?lang=${langCode}`;
}

/**
 * Remove tags SEO anteriores (pra evitar duplicação ao trocar página/idioma)
 */
function cleanupSeoTags(): void {
  document.querySelectorAll('[data-seo-i18n]').forEach(el => el.remove());
}

/**
 * Cria e insere um elemento no <head>
 */
function insertTag(tag: string, attrs: Record<string, string>): void {
  const el = document.createElement(tag);
  el.setAttribute('data-seo-i18n', 'true');
  for (const [k, v] of Object.entries(attrs)) {
    el.setAttribute(k, v);
  }
  document.head.appendChild(el);
}

export function SEOHead({ title, description, canonicalPath }: SEOHeadProps) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'pt-BR';
  const path = canonicalPath || window.location.pathname;

  useEffect(() => {
    cleanupSeoTags();

    // 1. Title
    if (title) {
      document.title = `${title} — WhiteLabelClaw`;
    }

    // 2. Meta description
    if (description) {
      insertTag('meta', { name: 'description', content: description });
    }

    // 3. hreflang pra cada idioma ativo
    for (const lang of ACTIVE_LANGUAGES) {
      insertTag('link', {
        rel: 'alternate',
        hreflang: lang.code,
        href: buildLangUrl(path, lang.code),
      });
    }

    // 4. x-default
    insertTag('link', {
      rel: 'alternate',
      hreflang: 'x-default',
      href: buildLangUrl(path, DEFAULT_LANG),
    });

    // 5. og:locale atual
    const currentOg = ACTIVE_LANGUAGES.find(l => l.code === currentLang)?.ogLocale
      || currentLang.replace('-', '_');
    insertTag('meta', { property: 'og:locale', content: currentOg });

    // 6. og:locale:alternate pra outros idiomas
    for (const lang of ACTIVE_LANGUAGES) {
      if (lang.code !== currentLang) {
        insertTag('meta', { property: 'og:locale:alternate', content: lang.ogLocale });
      }
    }

    // 7. og:title e og:description
    if (title) {
      insertTag('meta', { property: 'og:title', content: `${title} — WhiteLabelClaw` });
    }
    if (description) {
      insertTag('meta', { property: 'og:description', content: description });
    }

    // Cleanup ao desmontar
    return () => cleanupSeoTags();
  }, [currentLang, title, description, path]);

  // Componente não renderiza nada visível
  return null;
}

export default SEOHead;
